import { useState } from 'react';
import { useMutation } from 'urql';
import type { CrmIntegrationType } from '@shared';
import { getErrorMessage } from '@/lib/error-message';
import {
  AssignPhoneNumberCrmIntegrationMutation,
  CreateCrmIntegrationMutation,
  DeleteOrphanCrmIntegrationMutation,
  UpdateCrmIntegrationMutation,
} from './graphql';
import type { SystemOrganizationPhoneNumber } from './graphql';
import type { CaspioConfig } from './caspio-config';
import type { PlatformConfig } from './platform-config';

type CrmOverrideInput = { name: string; type: CrmIntegrationType; config: CaspioConfig | PlatformConfig };

export function useCrmOverrideActions(phoneNumber: SystemOrganizationPhoneNumber, onSaved: () => void) {
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [, runUpdateCrmIntegration] = useMutation(UpdateCrmIntegrationMutation);
  const [, runCreateCrmIntegration] = useMutation(CreateCrmIntegrationMutation);
  const [, runAssignCrmIntegration] = useMutation(AssignPhoneNumberCrmIntegrationMutation);
  const [, runDeleteOrphanCrmIntegration] = useMutation(DeleteOrphanCrmIntegrationMutation);

  const update = async (integrationId: string, input: CrmOverrideInput) => {
    const result = await runUpdateCrmIntegration({
      id: integrationId,
      name: input.name.trim(),
      config: input.config,
      updatedAt: new Date().toISOString(),
    });

    return result.error ? getErrorMessage(result.error, 'Failed to save the CRM override') : null;
  };

  const create = async (input: CrmOverrideInput) => {
    const created = await runCreateCrmIntegration({
      name: input.name.trim(),
      type: input.type,
      config: input.config,
    });

    const integrationId = created.data?.insertCrmIntegrationsOne?.id;

    if (!integrationId) return getErrorMessage(created.error, 'Failed to create the CRM override');

    const assigned = await runAssignCrmIntegration({
      phoneNumberId: phoneNumber.id,
      crmIntegrationId: integrationId,
      updatedAt: new Date().toISOString(),
    });

    if (!assigned.error) return null;

    await runDeleteOrphanCrmIntegration({ id: integrationId });

    return getErrorMessage(assigned.error, 'Failed to assign the CRM override to this phone number');
  };

  const save = async (input: CrmOverrideInput) => {
    setError(null);
    setIsSaving(true);

    const existing = phoneNumber.crmIntegration;
    const failure = existing ? await update(existing.id, input) : await create(input);

    setIsSaving(false);

    if (failure) {
      setError(failure);

      return;
    }

    onSaved();
  };

  return { error, isSaving, save };
}
